import clsx from "clsx";
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  MutableRefObject,
  useLayoutEffect,
} from "react";
import * as List from "@repo/ui/list";
import { useIntersectionObserver } from "@repo/ui/useIntersectionObserver";
import * as Viewport from "@/components/viewport";
import { SpacingVariants } from "../../../../../ui/src/types/spacing";
import ButtonIndicators from "../../carousel-button-indicator/button-indicators";
import { isMobile } from "../../utils";
import type { HorizontalAlignmentVariants } from "../types";
import styles from "./carousel.module.css";

type FeatureCarouselProps = {
  slides: JSX.Element[];
  slideRefs?: MutableRefObject<(HTMLLIElement | null)[]>;
  isFeatureCarousel?: boolean;
  slideGap?: SpacingVariants;
  videoPlayTrack?: { [key: string]: boolean };
  handleSetVideoPlayTrack?: (videoId: string) => void;
  horizontalAlignment?: HorizontalAlignmentVariants;
};

const horizontalAlignmentOptions = {
  "content-align-x-left": "justifyLeft",
  "content-align-x-center": "justifyCenter",
  "content-align-x-right": "justifyRight",
};

const totalSlidesWidth = (slideElements: NodeListOf<Element>): number => {
  return Array.from(slideElements).reduce(
    (width, slide) => width + slide.getBoundingClientRect().width,
    0,
  );
};

const getIsScrollable = (
  slideElements: NodeListOf<Element>,
  container: Element,
) => {
  return (
    totalSlidesWidth(slideElements) > container.getBoundingClientRect().width
  );
};

const Carousel = ({
  slides,
  slideRefs,
  videoPlayTrack,
  handleSetVideoPlayTrack,
  isFeatureCarousel = false,
  slideGap,
  horizontalAlignment,
}: FeatureCarouselProps) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isScrollable, setIsScrollable] = useState(true);
  const [showHorizontalScroll, setShowHorizontalScroll] = useState(false);

  const smallCarouselRef = useRef<HTMLUListElement>(null);
  const largeCarouselRef = useRef<HTMLUListElement>(null);

  const mobileView = isMobile();

  const observerCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach(({ isIntersecting, target }) => {
        if (target && typeof target.id === "string") {
          const index = Number(target.id);

          const slideElement = slideRefs?.current[index];
          const videoElement = slideElement?.querySelector?.("video");
          const videoId = videoElement?.id;

          if (isIntersecting) {
            setCurrentSlideIndex(index);
          }
          if (
            videoId &&
            videoPlayTrack &&
            handleSetVideoPlayTrack &&
            mobileView &&
            isFeatureCarousel &&
            isIntersecting !== undefined
          ) {
            handleSetVideoPlayTrack(videoId);
          }
        }
      });
    },
    [mobileView],
  );

  useIntersectionObserver({
    root: smallCarouselRef.current,
    rootMargin: "0px",
    threshold: 0.5,
    containerReference: smallCarouselRef.current || undefined,
    observableItems: slideRefs?.current as Element[] | undefined,
    callback: observerCallback,
  });

  const handleSlideIndexChange = useCallback((value: number) => {
    setCurrentSlideIndex(value);
  }, []);

  useLayoutEffect(() => {
    if (!smallCarouselRef.current) return;

    const slideElements = smallCarouselRef.current.querySelectorAll("li");
    const scrollable = getIsScrollable(slideElements, smallCarouselRef.current);
    setIsScrollable(scrollable);
  }, []);

  useEffect(() => {
    const container = largeCarouselRef.current;
    if (!container) return;

    const handleResize = () => {
      const slideElements = container.querySelectorAll("li");
      setShowHorizontalScroll(getIsScrollable(slideElements, container));
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [largeCarouselRef.current]);

  const horizontalAlignmentClass =
    horizontalAlignmentOptions[
      horizontalAlignment as keyof typeof horizontalAlignmentOptions
    ];

  return (
    <>
      <Viewport.Large>
        <List.Root
          style={{ gap: `var(--spacing-${slideGap})` }}
          ref={largeCarouselRef}
          snap
          snapOffset="var(--container-gutter)"
          className={clsx(
            styles.carouselContainer,
            showHorizontalScroll
              ? styles.justifyLeft
              : styles[horizontalAlignmentClass],
          )}
        >
          {slides}
        </List.Root>
      </Viewport.Large>
      <Viewport.Small>
        <List.Root
          ref={smallCarouselRef}
          snap
          snapOffset="var(--container-gutter)"
          className={clsx(styles.carouselContainer, {
            [styles.justifyCenter as string]:
              !isFeatureCarousel && !isScrollable,
          })}
          style={{
            gap: `var(--spacing-${slideGap})`,
          }}
        >
          {slides}
        </List.Root>
        {isFeatureCarousel && slideRefs && (
          <ButtonIndicators
            slideRefs={slideRefs}
            totalSlideCount={slides.length}
            currentSlideIndex={currentSlideIndex}
            handleSlideIndexChange={handleSlideIndexChange}
          />
        )}
      </Viewport.Small>
    </>
  );
};

export default Carousel;
